import { createFileRoute } from '@tanstack/react-router';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { BlogCard } from '@/components/blog/BlogCard';
import { BlogCardFeatured } from '@/components/blog/BlogCardFeatured';
import { Pagination } from '@/components/blog/Pagination';
import WhatsAppButton from '@/components/react/WhatsAppButton';
import { calculateReadingTime } from '@/lib/blog-utils';
import type { BlogPost } from '@/types/blog';

const POSTS_PER_PAGE = 9;

interface BlogData {
  posts: BlogPost[];
  featuredPost: BlogPost | null;
  regularPosts: BlogPost[];
  currentPage: number;
  totalPages: number;
}

async function loadBlogData(): Promise<BlogData> {
  const { getBlogPosts } = await import('@/lib/blog.server');
  const allPosts = await getBlogPosts();
  
  // Filter non-draft posts and sort by date
  const publishedPosts = allPosts
    .filter((post) => !post.frontmatter.draft)
    .sort(
      (a, b) =>
        new Date(b.frontmatter.publishDate).valueOf() -
        new Date(a.frontmatter.publishDate).valueOf()
    );

  const currentPage = 1;
  const totalPages = Math.ceil(publishedPosts.length / POSTS_PER_PAGE);
  const posts = publishedPosts.slice(0, POSTS_PER_PAGE);

  // Featured post (latest featured or just latest)
  const featuredPost = publishedPosts.find((p) => p.frontmatter.featured) || publishedPosts[0] || null;
  const regularPosts = featuredPost
    ? posts.filter((p) => p.slug !== featuredPost.slug).slice(0, 8)
    : posts;

  return {
    posts,
    featuredPost,
    regularPosts,
    currentPage,
    totalPages,
  };
}

export const Route = createFileRoute('/blog/')({
  loader: () => loadBlogData(),
  head: () => ({
    meta: [
      { title: 'Blog | Patriar - Holdings Patrimoniais e Contabilidade' },
      {
        name: 'description',
        content:
          'Artigos e guias sobre holdings patrimoniais, planejamento sucessório, tributação e contabilidade para famílias brasileiras.',
      },
      { property: 'og:title', content: 'Blog | Patriar - Holdings Patrimoniais e Contabilidade' },
      {
        property: 'og:description',
        content:
          'Artigos e guias sobre holdings patrimoniais, planejamento sucessório, tributação e contabilidade para famílias brasileiras.',
      },
    ],
  }),
  component: BlogPage,
});

function BlogPage() {
  const { featuredPost, regularPosts, currentPage, totalPages } = Route.useLoaderData();

  return (
    <>
      <Header />
      <main>
        {/* Hero Section */}
        <section className="py-16 lg:py-24 bg-neutral-50">
          <div className="container mx-auto px-4 lg:px-8">
            {/* Header */}
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-gold-600 font-semibold text-sm uppercase tracking-wider">
                Blog
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy-900 mt-3 mb-4">
                Conhecimento para proteger seu patrimônio
              </h1>
              <p className="text-lg text-neutral-600">
                Artigos, guias e dicas sobre holdings patrimoniais, planejamento sucessório e
                contabilidade.
              </p>
            </div>

            {/* Featured Post */}
            {featuredPost && (
              <div className="mb-12">
                <BlogCardFeatured
                  post={featuredPost}
                  readingTime={calculateReadingTime(featuredPost.content)}
                />
              </div>
            )}

            {/* Posts Grid */}
            {regularPosts.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {regularPosts.map((post) => (
                  <BlogCard
                    key={post.slug}
                    post={post}
                    readingTime={calculateReadingTime(post.content)}
                  />
                ))}
              </div>
            )}

            {/* Empty State */}
            {!featuredPost && regularPosts.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-gold-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <svg
                    className="w-8 h-8 text-gold-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-navy-900 mb-2">Em breve novos artigos</h2>
                <p className="text-neutral-600">
                  Estamos preparando conteúdos exclusivos para você. Volte em breve!
                </p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination currentPage={currentPage} totalPages={totalPages} baseUrl="/blog" />
            )}
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  );
}
